import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import {
  useUpdateElementData,
  useElementEnter,
  useElementPasteImage,
  useElementPasteBlocks,
  useElementPasteElements,
  useElementShiftArrow,
  useElementBackspaceStart,
  useAutoFocus,
  useSlashCommandTypes,
  useElementConvert,
  useElementArrow,
  useElementIndent,
  useRichTextExtensions,
  useRichTextSuggestionActiveRef,
  useRegisterRichTextEditor,
  useElementVirtualSelection,
} from "../DocumentView/DocumentContext";
import { usePageCollab } from "../DocumentView/PageCollabContext";
import type { PageRichTextProps } from "./PageRichText";

/**
 * Shared context wiring for every page-document text block whose content is
 * HTML stored in `data.content` (plain paragraph, list item, quote, callout, …).
 * It reads the edit-in-place write-path plus the Notion Enter/Backspace/autofocus
 * hooks and returns props ready to spread onto `<PageRichText>`. Each block just
 * decides its own surrounding chrome (bullet, number, checkbox, border, box).
 */
export function usePageTextBlock(element: DocumentElementType) {
  const data = element.data as { content?: string };
  const html = typeof data?.content === "string" ? data.content : "";
  const update = useUpdateElementData(element.id);
  const enter = useElementEnter(element.id);
  const pasteImage = useElementPasteImage(element.id);
  const pasteBlocks = useElementPasteBlocks(element.id);
  const pasteElements = useElementPasteElements(element.id);
  const shiftArrow = useElementShiftArrow(element.id);
  const backspaceStart = useElementBackspaceStart(element.id);
  const { shouldFocus, caret, coords, consume } = useAutoFocus(element.id);
  const slashTypes = useSlashCommandTypes();
  const convert = useElementConvert(element.id);
  const arrow = useElementArrow(element.id);
  const indent = useElementIndent(element.id);
  const extraExtensions = useRichTextExtensions();
  const suggestionActiveRef = useRichTextSuggestionActiveRef();
  const registerEditor = useRegisterRichTextEditor(element.id);
  const virtualSelection = useElementVirtualSelection(element.id);
  const pageCollab = usePageCollab();

  const richTextProps: PageRichTextProps = {
    value: html,
    editable: !!update,
    onChange: (newHtml) =>
      update?.((d) => {
        d.content = newHtml;
      }),
    autoFocus: shouldFocus ? { caret, coords } : null,
    onAutoFocused: consume,
    onEnter: enter
      ? (isEmpty, afterHtml, atStart) => enter({ isEmpty, afterHtml, atStart })
      : undefined,
    onPasteImage: pasteImage ?? undefined,
    onPasteBlocks: pasteBlocks ?? undefined,
    onPasteElements: pasteElements ?? undefined,
    onShiftArrow: shiftArrow ?? undefined,
    onBackspaceAtStart: backspaceStart ?? undefined,
    // The `/` slash menu converts this block into another type. Only wired when
    // the host supports it (page-document editor).
    slashTypes: slashTypes ?? undefined,
    onConvert: convert ?? undefined,
    // Cross-block arrow navigation (up/down/left/right off the edge).
    onArrow: arrow ?? undefined,
    onIndent: indent ?? undefined,
    // Host-injected extensions (e.g. the `@` entity mention).
    extraExtensions,
    suggestionActiveRef,
    // Cross-block text selection: registry handle + this block's share of the
    // virtual range (selection-look decorations).
    onEditorHandle: registerEditor,
    virtualSelection,
    // Live-collab binding (null unless the host document is collaborative and
    // this block has a content fragment).
    collab: (() => {
      const fragment = pageCollab?.getContentFragment(element.id);
      return pageCollab && fragment
        ? {
            fragment,
            provider: pageCollab.provider,
            user: pageCollab.user,
            // Presence: broadcast which block this user is editing.
            onFocusChange: pageCollab.setEditingBlockId
              ? (focused: boolean) =>
                  pageCollab.setEditingBlockId?.(focused ? element.id : null)
              : undefined,
          }
        : null;
    })(),
  };

  return { html, editable: !!update, update, richTextProps };
}
